(ns measure.report-builder.context
  (:require [common.form :as form]
            [common.queries :refer [use-mutation]]
            [common.util :as util]
            [measure.queries :as q]
            [measure.report-builder.core :as rbc]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def report-builder-context (uix/create-context nil))

(defhook use-schedule-report-form [{:keys [report]}]
  (let [url-context (use-subscribe [:url-context])

        {:keys [handleSubmit] :as form-control}
        (form/use-form #js {:mode "onChange"
                            :defaultValues (util/spread-obj rbc/default-values (rbc/->form report))
                            :resolver (form/use-malli-resolver (rbc/schema))})

        {create-report-schedule :mutate} (use-mutation (q/create-report-schedule-mutation url-context))

        on-submit (uix/use-callback
                   (fn [values]
                     (let [payload (rbc/form->payload values :report-schedule)]
                       (create-report-schedule payload
                                               #js {:onSuccess (fn []
                                                                 (rf/dispatch [:modal/close])
                                                                 (rfe/navigate :advertiser/reports
                                                                               {:path-params url-context
                                                                                :fragment "report-schedules"}))})))
                   [url-context create-report-schedule])]
    {:form-control form-control
     :on-submit (handleSubmit on-submit)}))

(defui provider [{:keys [children]}]
  (let [url-context (use-subscribe [:url-context])

        [step set-step] (uix/use-state nil) ; nil, :scope, or :content
        [entity set-entity] (uix/use-state nil) ; report, report-schedule
        [mode set-mode] (uix/use-state :create)
        [initial-values set-initial-values] (uix/use-state nil)

        {:keys [handleSubmit reset ^js formState] :as form-control}
        (form/use-form #js {:mode "onChange"
                            :defaultValues rbc/default-values
                            :resolver (form/use-malli-resolver (rbc/schema (= step :content)))})

        {create-unified-report :mutate} (use-mutation (q/create-unified-report-mutation url-context))
        {create-report-schedule :mutate} (use-mutation (q/create-report-schedule-mutation url-context))
        {update-report-schedule :mutate} (use-mutation (q/update-report-schedule-mutation url-context))

        mutation-fn (uix/use-memo
                     (fn []
                       (cond
                         (= entity :report) create-unified-report
                         (and (= entity :report-schedule) (= mode :create)) create-report-schedule
                         :else update-report-schedule))
                     [entity mode create-unified-report create-report-schedule update-report-schedule])

        on-close (uix/use-callback
                  (fn []
                    (set-step nil)
                    (set-entity nil)
                    (set-mode nil)
                    (set-initial-values nil))
                  [])

        on-submit (uix/use-callback
                   (fn [values]
                     (case step
                       :scope (set-step :content)
                       (let [payload (rbc/form->payload values entity)]
                         (mutation-fn payload #js {:onSuccess on-close}))))
                   [step entity mutation-fn on-close])

        open-report-builder (uix/use-callback
                             (fn [{:keys [entity mode initial-value]}]
                               (set-step :scope)
                               (set-entity entity)
                               (set-mode mode)
                               (when initial-value
                                 (set-initial-values (rbc/->form initial-value))))
                             [])

        ;; In :create mode, the first submit advances from :scope to :content step,
        ;; not an actual content submission, so subtract 1 to reflect real submit attempts.
        ;; In :edit mode, the :scope step is skipped, so no adjustment is needed.
        submit-count (cond-> (.-submitCount formState)
                       (= mode :create) dec)

        context-value (uix/use-memo
                       (fn []
                         {:step step
                          :entity entity
                          :mode mode
                          :open-report-builder open-report-builder
                          :on-submit (handleSubmit on-submit)
                          :on-close on-close
                          :submit-count submit-count
                          :form-control form-control})
                       [step entity mode form-control handleSubmit on-submit on-close submit-count open-report-builder])

        initialised? (uix/use-ref false)]
    (uix/use-effect (fn []
                      ;; On :scope step, initialise form once per opening.
                      ;; When `initial-values` is passed to `open-report-builder`,
                      ;; we want to include those values in the initialised form.
                      (when (and (= step :scope) (not @initialised?))
                        (reset! initialised? true)
                        (reset (util/spread-obj rbc/default-values initial-values)))

                      ;; Reset the ref when step changes away from :scope
                      (when (not= step :scope)
                        (reset! initialised? false)))
                    [step reset initial-values])
    ($ (.-Provider report-builder-context)
       {:value context-value}
       children)))

(defhook use-report-builder-context []
  (uix/use-context report-builder-context))
